import { FileSpreadsheet, FileText, Layers, ListChecks, Coins, Package, ShoppingCart, Truck, Users } from 'lucide-react';
import type { AuthUser } from '@/stores/authStore';

export type ItemMenu = {
  rotulo: string;
  path: string;
  icone: typeof FileText;
  permissao?: string;
  // Item de outro módulo que aparece aqui porque faz parte do trabalho deste.
  modulo?: string;
  // Abre o índice por cima da tela atual, sem navegar.
  modal?: boolean;
};

export type Modulo = {
  nome: string;
  // Título curto do grupo de telas do módulo, logo abaixo do seletor.
  secao: string;
  // Uma linha para o cartão do módulo na página inicial.
  descricao: string;
  icone: typeof FileText;
  itens: ItemMenu[];
};

/**
 * Os menus pertencem ao módulo escolhido. É assim que Cotação e Folha vão
 * conviver sem virar uma lista única e comprida — e é o que permite vender
 * cada módulo separadamente. O índice de metais LME não é módulo: é uma tela
 * de apoio que aparece dentro da cotação, então fica fora do seletor.
 *
 * Módulo novo entra aqui (e no ModuleCatalog do backend): o seletor do menu e
 * a vitrine da página inicial passam a mostrá-lo sozinhos.
 */
export const MODULOS: Record<string, Modulo> = {
  cotacao: {
    nome: 'Cotação e compras',
    secao: 'Cotação',
    descricao: 'Propostas dos fornecedores, equalização, negociação e fechamento do pacote.',
    icone: ShoppingCart,
    itens: [
      { rotulo: 'Cotações', path: '/cotacoes', icone: FileText, permissao: 'cotacao.ver' },
      { rotulo: 'Produtos', path: '/produtos', icone: Package, permissao: 'cotacao.ver' },
      { rotulo: 'Fornecedores', path: '/fornecedores', icone: Truck, permissao: 'cotacao.ver' },
      // O comprador consulta o cobre no meio da negociação, sem trocar de módulo nem de tela.
      { rotulo: 'Índice de metais LME', path: '/indices', icone: Coins, permissao: 'indices.ver', modulo: 'indices', modal: true },
    ],
  },
  folha: {
    nome: 'RH e folha',
    secao: 'Folha',
    descricao: 'Do PDF do escritório à planilha de lançamentos do ERP, conferida por setor.',
    icone: Users,
    itens: [
      { rotulo: 'Folha por setor', path: '/folha', icone: FileSpreadsheet, permissao: 'folha.ver' },
      // A tela é toda de configuração, e a própria listagem exige a permissão: pedir 'folha.ver' abriria a tela para levar 403.
      { rotulo: 'Centros de custo', path: '/centros-de-custo', icone: Layers, permissao: 'folha.configurar' },
      { rotulo: 'Rubricas e verbas', path: '/rubricas-e-verbas', icone: ListChecks, permissao: 'folha.ver' },
    ],
  },
};

/** A pessoa chega na tela: tem a permissão e, se o item é de outro módulo, esse módulo contratado. */
export function alcancaItem(user: AuthUser | null, item: ItemMenu): boolean {
  const permissoes = user?.permissoes ?? [];
  const ativos = user?.modulos_ativos ?? [];

  return (!item.permissao || permissoes.includes(item.permissao)) && (!item.modulo || ativos.includes(item.modulo));
}

/** A lista principal do módulo — Cotações, ou Folha por setor —: a primeira tela do menu que a pessoa alcança. */
export function telaPrincipal(user: AuthUser | null, slug: string): string | null {
  return MODULOS[slug]?.itens.find((item) => !item.modal && alcancaItem(user, item))?.path ?? null;
}
